/**
 * Projectile.ts
 *
 * Base class for all projectiles in the game.
 * Handles movement, collision, and lifecycle.
 */

import Phaser from 'phaser';
import { Entity, EntityConfig } from '../entities/base/Entity';
import { Allegiance } from '../weapons/DataManagerWeapon';
import { ProjectileConfig } from './ProjectileTypes';
import { DataManagerProjectile, ProjectileDefinition } from './DataManagerProjectile';
import { EffectsSystem } from '../systems/EffectsSystem';
import { audioManager } from '../core/CoreManagerAudio';
import { LayerDepths } from '../constants/enums';

export interface ProjectileEntityConfig extends EntityConfig {
  projectileType: string;
  angle: number;
  speed?: number;
  damage?: number;
  shooterId?: number;
  targetId?: number;
  initialScaleX?: number;
  initialScaleY?: number;
  // Travel phase (for delayedLock with initial movement)
  travelTargetX?: number;
  travelTargetY?: number;
  travelSpeed?: number;
  travelStartScale?: number;  // Scale at spawn (small)
  travelEndScale?: number;    // Scale at destination (full size)
}

/**
 * Projectile - Base class for all projectiles
 *
 * Supports different movement behaviors:
 * - bullet: straight line movement
 * - homing: tracks a target
 * - gravity: affected by gravity
 * - static: stays in place (mines)
 */
export class Projectile extends Entity {
  /** Visual offset for floor impact effects to reach the grass */
  private static readonly GROUND_VISUAL_OFFSET = 5;

  // Projectile properties
  public projectileType: string;
  public definition: ProjectileDefinition;
  public damage: number;
  public attackType: string;
  public autoDestroy: boolean;
  public destructOnFloor: boolean;
  public abductable: boolean;
  public abductedState: 'normal' | 'abducted' = 'normal';

  // Movement
  public speed: number;
  public moveAngle: number;

  // Homing
  public targetId?: number;
  private homingTarget?: Entity;
  private currentSpeed: number;

  // Lifecycle
  public shooterId?: number;
  public spawnTime: number = 0;
  public duration: number;
  private isActive: boolean = false;

  // Expansion
  private baseScaleX: number = 1;
  private baseScaleY: number = 1;
  private expansionProgress: number = 0;  // 0-1

  // Delayed lock behavior (orbs that wait, lock, then fire)
  // States: traveling -> waiting -> fired
  private delayedLockState: 'traveling' | 'waiting' | 'fired' = 'waiting';
  private delayedLockTimer: number = 0;

  // Travel phase properties (for delayedLock with initial movement)
  private travelTargetX: number = 0;
  private travelTargetY: number = 0;
  private travelSpeed: number = 300;
  private travelStartScale: number = 0.1;
  private travelEndScale: number = 1;
  private travelStartX: number = 0;
  private travelStartY: number = 0;
  private hasTravelPhase: boolean = false;

  // Destructible projectiles
  public hp: number = 0;
  public destructible: boolean = false;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    config: ProjectileEntityConfig
  ) {
    const definition = DataManagerProjectile.get(config.projectileType);
    if (!definition) {
      throw new Error(`Unknown projectile type: ${config.projectileType}`);
    }

    super(scene, x, y, definition.texture, {
      ...config,
      allegiance: config.allegiance ?? Allegiance.Player,
    });

    this.projectileType = config.projectileType;
    this.definition = definition;

    // Apply definition values (can be overridden by config)
    this.damage = config.damage ?? definition.damage;
    this.attackType = definition.attackType;
    this.autoDestroy = definition.autoDestroy;
    this.destructOnFloor = definition.destructOnFloor;
    this.abductable = definition.abductable;
    this.duration = definition.duration;

    // Movement
    this.speed = config.speed ?? definition.speed;
    this.currentSpeed = this.speed;
    this.moveAngle = config.angle;

    // References
    this.shooterId = config.shooterId;
    this.targetId = config.targetId;

    // Travel phase initialization (for delayedLock with initial movement)
    if (config.travelTargetX !== undefined && config.travelTargetY !== undefined) {
      this.hasTravelPhase = true;
      this.travelTargetX = config.travelTargetX;
      this.travelTargetY = config.travelTargetY;
      this.travelSpeed = config.travelSpeed ?? 300;
      this.travelStartScale = config.travelStartScale ?? 0.1;
      this.travelEndScale = config.travelEndScale ?? (definition.scale ?? 1);
      this.travelStartX = x;
      this.travelStartY = y;
    }

    // Setup physics
    this.setupPhysics();

    // Apply scale - travel phase starts small, otherwise use config or definition
    if (this.hasTravelPhase) {
      this.setScale(this.travelStartScale);
    } else if (config.initialScaleX !== undefined || config.initialScaleY !== undefined) {
      const sx = config.initialScaleX ?? definition.scale ?? 1;
      const sy = config.initialScaleY ?? definition.scale ?? 1;
      this.setScale(sx, sy);
    } else if (definition.scale) {
      this.setScale(definition.scale);
    }

    // Store base scale for expansion calculations
    this.baseScaleX = this.scaleX;
    this.baseScaleY = this.scaleY;

    // Apply origin: use definition override, fallback to C3 data
    const c3Origin = DataManagerProjectile.getOrigin(definition.texture);
    const originX = definition.originX ?? c3Origin.x;
    const originY = definition.originY ?? c3Origin.y;
    this.setOrigin(originX, originY);

    // Update body size to match scaled display size
    this.syncBodyToVisualSize();

    // Enable animation listeners for per-frame hitbox (projectiles are always "in view")
    this.onEnterView();

    // Listen for animation complete (projectile-specific)
    this.on('animationcomplete', this.onAnimationComplete, this);

    // Set specific frame if defined (overrides animation)
    if (definition.frame) {
      this.setFrame(definition.frame);
    } else if (definition.animation) {
      // Play animation if defined and no static frame specified
      // Normalize animation key to lowercase per project standards
      const animKey = Entity.normalize(definition.animation);
      if (this.scene.anims.exists(animKey)) {
        this.play(animKey);
      } else {
        console.warn(`[Projectile] Animation "${animKey}" not found`);
      }
    }

    // Set initial angle for sprite (sprites point right = 0° by default)
    this.setRotation(Phaser.Math.DegToRad(this.moveAngle));
  }

  /**
   * Setup physics body
   */
  private setupPhysics(): void {
    const body = this.body as Phaser.Physics.Arcade.Body;
    if (!body) return;

    // Most projectiles don't use gravity
    if (this.definition.behavior !== 'gravity') {
      body.setAllowGravity(false);
    }

    // Static projectiles don't move
    if (this.definition.behavior === 'static') {
      body.setVelocity(0, 0);
      body.setImmovable(true);
    }
  }

  /**
   * Update physics body to match sprite visual size.
   *
   * For line collision projectiles (e.g., laser), we use a small fixed-size body
   * centered on the origin (tip) using offset. Phaser applies scale internally.
   */
  private syncBodyToVisualSize(): void {
    const body = this.body as Phaser.Physics.Arcade.Body;
    if (!body) return;

    const frame = this.frame;

    if (this.definition.collisionType === 'line') {
      const tipSize = 16;
      // setSize uses unscaled values, Phaser applies scale internally
      const unscaledSize = tipSize / this.scaleX;
      body.setSize(unscaledSize, unscaledSize);
      // Offset to center body on the origin (tip of the laser)
      const offsetX = this.originX * frame.width - unscaledSize / 2;
      const offsetY = this.originY * frame.height - unscaledSize / 2;
      body.setOffset(offsetX, offsetY);
    } else {
      body.setSize(frame.width, frame.height);
      body.setOffset(0, 0);
    }
  }

  /**
   * Override to prevent animation frames from resetting body size
   * for line collision projectiles (they use fixed 16x16 body at tip)
   */
  protected updatePhysicsBodyFromHitbox(hitbox: import('../systems/DataManagerCollision').BoundingBox): void {
    // Line collision projectiles keep their fixed body size
    if (this.definition.collisionType === 'line') {
      return; // Skip - keep the 16x16 body we set in syncBodyToVisualSize()
    }
    // Other projectiles use normal per-frame hitbox
    super.updatePhysicsBodyFromHitbox(hitbox);
  }

  /**
   * Activate the projectile (called when retrieved from pool)
   */
  activate(): void {
    this.isActive = true;
    this.setActive(true);
    this.setVisible(true);
    this.setDepth(LayerDepths.Projectiles);
    this.spawnTime = this.scene.time.now;

    // Ensure physics body is enabled
    const body = this.body as Phaser.Physics.Arcade.Body;
    if (body) {
      body.enable = true;
    }
    this.abductedState = 'normal';
    this.currentSpeed = this.speed;

    // Reset delayed lock state
    this.delayedLockTimer = 0;

    // Initialize destructible properties
    this.destructible = this.definition.destructible ?? false;
    this.hp = this.definition.hp ?? 0;

    // Handle delayedLock with optional travel phase
    if (this.definition.behavior === 'delayedLock') {
      if (this.hasTravelPhase) {
        // Start in traveling state, moving toward target
        this.delayedLockState = 'traveling';
        this.setScale(this.travelStartScale);
        // Calculate angle to travel target
        const travelAngle = Phaser.Math.Angle.Between(
          this.x, this.y, this.travelTargetX, this.travelTargetY
        );
        this.setVelocityFromAngle(Phaser.Math.RadToDeg(travelAngle), this.travelSpeed);
      } else {
        // No travel phase, start waiting
        this.delayedLockState = 'waiting';
      }
    } else if (this.definition.behavior !== 'static') {
      // Normal projectiles start moving immediately
      this.setVelocityFromAngle(this.moveAngle, this.currentSpeed);
    }

    // Play spawn sound via audioManager
    if (this.definition.spawnSound) {
      audioManager.playSfx(this.definition.spawnSound, 'projectile', this.scene);
    }
  }

  /**
   * Set velocity from angle and speed
   */
  private setVelocityFromAngle(angleDeg: number, speed: number): void {
    const body = this.body as Phaser.Physics.Arcade.Body;
    if (!body) return;

    const angleRad = Phaser.Math.DegToRad(angleDeg);
    body.setVelocity(
      Math.cos(angleRad) * speed,
      Math.sin(angleRad) * speed
    );
  }

  /**
   * Set homing target
   */
  setTarget(target: Entity): void {
    this.homingTarget = target;
    this.targetId = target.entityId ? parseInt(target.entityId) : undefined;
  }

  /**
   * Update projectile each frame
   */
  update(time: number, delta: number): void {
    if (!this.isActive || !this.active || !this.scene) return;

    super.update(time, delta);

    // Check duration
    if (this.duration > 0 && time - this.spawnTime > this.duration) {
      this.onDurationExpired();
      return;
    }

    // Handle expansion over time
    if (this.definition.expansion) {
      this.updateExpandingSprite(time);
    }

    // Handle abducted state
    if (this.abductedState === 'abducted') {
      this.updateAbducted(delta);
      return;
    }

    // Handle movement behavior
    switch (this.definition.behavior) {
      case 'homing':
        this.updateHoming(delta);
        break;
      case 'bullet':
        this.updateRotation();
        break;
      case 'gravity':
        this.updateGravity();
        break;
      case 'delayedLock':
        this.updateDelayedLock(delta);
        break;
      // static: no update needed
    }
  }

  /**
   * Update homing movement
   */
  private updateHoming(delta: number): void {
    if (!this.homingTarget || !this.homingTarget.active) {
      // Lost target, continue straight
      return;
    }

    const body = this.body as Phaser.Physics.Arcade.Body;
    if (!body) return;

    // Calculate angle to target
    const targetAngle = Phaser.Math.Angle.Between(
      this.x,
      this.y,
      this.homingTarget.x,
      this.homingTarget.y
    );

    // Current angle
    const currentAngle = Phaser.Math.DegToRad(this.moveAngle);

    // Turn towards target
    const turnRate = Phaser.Math.DegToRad(this.definition.homingTurnRate ?? 90);
    const maxTurn = turnRate * (delta / 1000);

    let angleDiff = targetAngle - currentAngle;
    // Normalize to -PI to PI
    while (angleDiff > Math.PI) angleDiff -= Math.PI * 2;
    while (angleDiff < -Math.PI) angleDiff += Math.PI * 2;

    // Clamp turn amount
    const turn = Phaser.Math.Clamp(angleDiff, -maxTurn, maxTurn);
    const newAngle = currentAngle + turn;

    this.moveAngle = Phaser.Math.RadToDeg(newAngle);

    // Accelerate if defined
    if (this.definition.homingAcceleration) {
      this.currentSpeed += this.definition.homingAcceleration * (delta / 1000);
      // Cap at reasonable max
      this.currentSpeed = Math.min(this.currentSpeed, 1000);
    }

    // Apply velocity
    this.setVelocityFromAngle(this.moveAngle, this.currentSpeed);

    // Update sprite rotation to match movement direction
    this.setRotation(newAngle);
  }

  /**
   * Update rotation for spinning projectiles
   */
  private updateRotation(): void {
    if (this.definition.rotationSpeed) {
      this.rotation += Phaser.Math.DegToRad(this.definition.rotationSpeed) * (1 / 60);
    }
  }

  /**
   * Update gravity-affected projectiles
   */
  private updateGravity(): void {
    // Sprite rotation follows velocity
    const body = this.body as Phaser.Physics.Arcade.Body;
    if (body && (body.velocity.x !== 0 || body.velocity.y !== 0)) {
      this.rotation = Math.atan2(body.velocity.y, body.velocity.x) - Math.PI / 2;
    }
  }

  /**
   * Update delayed lock behavior (orbs that travel, wait, lock on player, then fire)
   * - traveling: moves toward target position while growing
   * - waiting: floats in place, incrementing timer
   * - fired: moves in straight line toward locked position
   */
  private updateDelayedLock(delta: number): void {
    const body = this.body as Phaser.Physics.Arcade.Body;
    if (!body) return;

    if (this.delayedLockState === 'traveling') {
      // Check if we've reached the target position
      const distToTarget = Phaser.Math.Distance.Between(
        this.x, this.y, this.travelTargetX, this.travelTargetY
      );

      if (distToTarget < 10) {
        // Arrived at target, switch to waiting
        this.delayedLockState = 'waiting';
        body.setVelocity(0, 0);
        this.setScale(this.travelEndScale);
        this.delayedLockTimer = 0;
      } else {
        // Still traveling - update scale based on progress
        const totalDist = Phaser.Math.Distance.Between(
          this.travelStartX, this.travelStartY, this.travelTargetX, this.travelTargetY
        );
        const traveled = totalDist - distToTarget;
        const progress = Math.min(1, traveled / totalDist);

        // Interpolate scale from start to end
        const currentScale = this.travelStartScale + (this.travelEndScale - this.travelStartScale) * progress;
        this.setScale(currentScale);
      }
    } else if (this.delayedLockState === 'waiting') {
      // Stay in place while waiting
      body.setVelocity(0, 0);
      this.delayedLockTimer += delta;

      const lockDelay = this.definition.delayedLockDelay ?? 800;
      if (this.delayedLockTimer >= lockDelay) {
        // Time to lock on player and fire
        const player = this.scene.registry.get('player') as Phaser.GameObjects.Sprite | undefined;
        if (player) {
          // Calculate angle to player position at this moment
          const angle = Phaser.Math.Angle.Between(this.x, this.y, player.x, player.y);
          this.moveAngle = Phaser.Math.RadToDeg(angle);
          this.setRotation(angle);
        }

        // Switch to fired state with locked speed
        this.delayedLockState = 'fired';
        const lockSpeed = this.definition.delayedLockSpeed ?? this.speed;
        this.setVelocityFromAngle(this.moveAngle, lockSpeed);
      }
    }
    // In 'fired' state, projectile just continues in straight line (velocity already set)
  }

  /**
   * Update expansion over time (for wave-like projectiles)
   * Grows the projectile from base scale to maxScale over duration
   *
   * Note: When sprite is rotated ~90° or ~270°, we swap scale axes
   * so that "maxScaleX" always means visual horizontal growth
   */
  private updateExpandingSprite(time: number): void {
    const expansion = this.definition.expansion;
    if (!expansion) return;

    const elapsed = time - this.spawnTime;
    this.expansionProgress = Math.min(1, elapsed / expansion.duration);

    // Get max scales (support both old and new format)
    const maxScaleX = expansion.maxScaleX ?? expansion.maxScale ?? 1;
    const maxScaleY = expansion.maxScaleY ?? expansion.maxScale ?? 1;

    // Calculate multipliers (1 to maxScale)
    const multiplierX = 1 + (maxScaleX - 1) * this.expansionProgress;
    const multiplierY = 1 + (maxScaleY - 1) * this.expansionProgress;

    // Check if sprite is rotated ~90° or ~270° (vertical orientation)
    // When rotated, sprite's local X axis points vertically, so we swap
    const angleDeg = Phaser.Math.RadToDeg(this.rotation);
    const normalizedAngle = ((angleDeg % 360) + 360) % 360;
    const isVertical = (normalizedAngle > 45 && normalizedAngle < 135) ||
                       (normalizedAngle > 225 && normalizedAngle < 315);

    // Swap scale axes when rotated vertically so config is intuitive:
    // maxScaleX = visual horizontal growth, maxScaleY = visual vertical growth
    if (isVertical) {
      this.scaleX = this.baseScaleX * multiplierY;
      this.scaleY = this.baseScaleY * multiplierX;
    } else {
      this.scaleX = this.baseScaleX * multiplierX;
      this.scaleY = this.baseScaleY * multiplierY;
    }

    // Update collision body to match new scale
    this.updateExpandingHitbox();
  }

  /**
   * Update physics body size for expanding projectiles.
   *
   * Phaser Arcade Physics applies scaleX to body width and scaleY to body height,
   * regardless of sprite rotation. When the sprite is rotated ~90°, the visual
   * width is displayHeight. We calculate the desired world-space hitbox size,
   * then "undo" the scale so Phaser's automatic scaling gives us the right result.
   */
  private updateExpandingHitbox(): void {
    const body = this.body as Phaser.Physics.Arcade.Body;
    if (!body) return;

    const expansion = this.definition.expansion;
    if (!expansion) return;

    const hitboxWidthRatio = 0.6;
    const hitboxHeightRatio = 0.3;  // Smaller vertical hitbox

    // Detect if sprite is rotated ~90° or ~270° (vertical orientation)
    const angleDeg = Phaser.Math.RadToDeg(this.rotation);
    const normalizedAngle = ((angleDeg % 360) + 360) % 360;
    const isVertical = (normalizedAngle > 45 && normalizedAngle < 135) ||
                       (normalizedAngle > 225 && normalizedAngle < 315);

    // Calculate desired hitbox size in WORLD coordinates
    // When rotated 90°, displayHeight is the visual width
    let desiredWidth: number;
    let desiredHeight: number;

    if (isVertical) {
      desiredWidth = this.displayHeight * hitboxWidthRatio;
      desiredHeight = this.displayWidth * hitboxHeightRatio;
    } else {
      desiredWidth = this.displayWidth * hitboxWidthRatio;
      desiredHeight = this.displayHeight * hitboxHeightRatio;
    }

    // Phaser will apply scale to body, so we "undo" it here
    // Final body size = bodyWidth * scaleX, bodyHeight * scaleY
    const bodyWidth = desiredWidth / this.scaleX;
    const bodyHeight = desiredHeight / this.scaleY;

    body.setSize(bodyWidth, bodyHeight);

    // Center the hitbox on the frame
    const frame = this.frame;
    const offsetX = (frame.width - bodyWidth) / 2;
    const offsetY = (frame.height - bodyHeight) / 2;
    body.setOffset(offsetX, offsetY);
  }

  /**
   * Update when abducted by tractor beam
   */
  private updateAbducted(delta: number): void {
    // Spin when abducted
    this.rotation += Phaser.Math.DegToRad(30);

    // Speed up
    const body = this.body as Phaser.Physics.Arcade.Body;
    if (body) {
      const currentSpeed = body.velocity.length();
      const newSpeed = currentSpeed + 100;
      body.velocity.normalize().scale(newSpeed);
    }
  }

  /**
   * Resolve impact material for per-material hit effects.
   */
  private resolveImpactMaterial(target?: Entity): string | undefined {
    if (!target?.config) return undefined;
    const directMaterial = target.config.hitMaterial as string | undefined;
    if (directMaterial) return directMaterial.toLowerCase();

    const category = target.config.category as string | undefined;
    if (category === 'structure') {
      const type = ((target.config.type as string | undefined) || target.texture?.key || '').toLowerCase();
      if (type.includes('store')) return 'glass';
      if (type.includes('house')) return 'wood';
      return 'wood';
    }
    return undefined;
  }

  /**
   * Particles only for structure impacts with bomb-slot projectiles (key C weapons).
   */
  private shouldSpawnStructureImpactParticles(target?: Entity): boolean {
    if (!target?.config) return false;
    const category = target.config.category as string | undefined;
    if (category !== 'structure') return false;

    const bombSlotProjectiles = new Set(['plasmabomb', 'missile', 'mine']);
    return bombSlotProjectiles.has(this.projectileType.toLowerCase()) && this.damage >= 5;
  }

  /**
   * Called when projectile hits something
   */
  handleEntityCollision(target?: Entity): void {
    // Check scene exists (may be destroyed during restart)
    if (!this.scene) return;

    // console.log('[Projectile.handleEntityCollision]', this.projectileType, 'hit at', this.x, this.y);

    // Play hit sound via audioManager
    if (this.definition.hitSound) {
      audioManager.playSfx(this.definition.hitSound, 'projectile', this.scene);
    }

    // Get impact position (tip for line collision, center otherwise)
    const { x: impactX, y: impactY } = this.getTipPosition();

    // Spawn effect using data-driven system
    const fx = this.definition.effects?.onHit;
    // console.log('[Projectile.handleEntityCollision] fx:', fx);
    if (fx) {
      const effectsSystem = this.scene.registry.get('effectsSystem') as EffectsSystem | undefined;
      const material = this.resolveImpactMaterial(target);
      const allowParticles = this.shouldSpawnStructureImpactParticles(target);
      // console.log('[Projectile.handleEntityCollision] effectsSystem:', effectsSystem ? 'found' : 'NOT FOUND');
      effectsSystem?.spawnFromEvent(this.definition.effects, 'onHit', impactX, impactY, material, allowParticles);
    }

    // Handle AOE damage if defined
    if (this.definition.aoeRadius && this.definition.aoeRadius > 0) {
      this.scene.events.emit('aoeExplosion', {
        x: impactX,
        y: impactY,
        radius: this.definition.aoeRadius,
        damage: this.definition.aoeDamage ?? this.damage,
        duration: this.definition.aoeDuration ?? 0,  // 0 = instantaneous
        tint: this.definition.aoeTint,
        allegiance: this.allegiance,
        sourceTarget: target,  // Exclude the direct hit target from AOE
        attackType: this.definition.attackType,  // Pass attack type for weakTo check
      });
    }

    // Emit hit event
    this.scene.events.emit('projectileHit', {
      projectile: this,
      target,
      damage: this.damage,
      x: impactX,
      y: impactY,
    });

    // Destroy if autoDestroy
    if (this.autoDestroy) {
      this.returnToPool();
    }
  }

  /**
   * Take damage from player projectile (for destructible projectiles)
   * @returns true if projectile was destroyed
   */
  takeDamage(amount: number): boolean {
    if (!this.destructible || !this.active) return false;

    this.hp -= amount;

    // Visual feedback: flash red
    this.setTint(0xff4444);
    this.scene.time.delayedCall(80, () => {
      if (this.active) this.clearTint();
    });

    if (this.hp <= 0) {
      // Play destroy animation if exists
      const destroyAnim = `${this.definition.texture}_Destroy`;
      if (this.scene.anims.exists(destroyAnim)) {
        // Support override frameRate for faster death animations
        const frameRate = this.definition.deathAnimFrameRate;
        if (frameRate) {
          this.play({ key: destroyAnim, frameRate }, true);
        } else {
          this.play(destroyAnim, true);
        }
        this.once('animationcomplete', () => {
          this.returnToPool();
        });
      } else {
        // No destroy animation, spawn hit effect and despawn
        const fx = this.definition.effects?.onHit;
        if (fx) {
          const effectsSystem = this.scene.registry.get('effectsSystem') as EffectsSystem | undefined;
          effectsSystem?.spawn(fx, this.x, this.y);
        }
        this.returnToPool();
      }
      return true;
    }

    return false;
  }

  /**
   * Called when projectile hits the ground
   */
  handleGroundCollision(): void {
    // Check scene exists (may be destroyed during restart)
    if (!this.scene) return;

    if (this.destructOnFloor) {
      // If floor penetration is defined, delay destruction to allow visual sinking
      const penetration = this.definition.floorPenetration ?? 0;
      if (penetration > 0) {
        // Calculate delay based on speed and penetration depth
        const body = this.body as Phaser.Physics.Arcade.Body;
        const verticalSpeed = body ? Math.abs(body.velocity.y) : this.speed;
        const delayMs = (penetration / verticalSpeed) * 1000;

        // Schedule destruction after penetration
        this.scene.time.delayedCall(delayMs, () => {
          if (this.active) {
            this.destroyOnFloor();
          }
        });
      } else {
        this.destroyOnFloor();
      }
    }
  }

  /**
   * Internal: Spawn floor hit effects and destroy projectile
   */
  private destroyOnFloor(): void {
    // Check scene exists (may be destroyed during restart)
    if (!this.scene) return;

    const { x: impactX, y: impactY } = this.getTipPosition();

    // Use onHitFloor effect if defined, otherwise fallback to onHit
    const fx = this.definition.effects?.onHitFloor || this.definition.effects?.onHit;
    if (fx) {
      const effectsSystem = this.scene.registry.get('effectsSystem') as EffectsSystem | undefined;
      // Add visual offset to reach the grass
      effectsSystem?.spawnFromEvent(
        this.definition.effects,
        this.definition.effects?.onHitFloor ? 'onHitFloor' : 'onHit',
        impactX,
        impactY + Projectile.GROUND_VISUAL_OFFSET,
        undefined,
        false
      );
    }

    // Handle AOE damage on floor hit
    if (this.definition.aoeRadius && this.definition.aoeRadius > 0) {
      this.scene.events.emit('aoeExplosion', {
        x: impactX,
        y: impactY,
        radius: this.definition.aoeRadius,
        damage: this.definition.aoeDamage ?? this.damage,
        duration: this.definition.aoeDuration ?? 0,
        tint: this.definition.aoeTint,
        allegiance: this.allegiance,
        sourceTarget: undefined,  // No direct hit target on floor
        attackType: this.definition.attackType,  // Pass attack type for weakTo check
      });
    }

    this.returnToPool();
  }

  /**
   * Get the projectile tip position (used for effects and collision).
   * Returns sprite origin position since origin is set to the tip.
   */
  private getTipPosition(): { x: number; y: number } {
    return { x: this.x, y: this.y };
  }

  /**
   * Start abduction by tractor beam
   */
  startAbduction(): void {
    if (!this.abductable) return;

    this.abductedState = 'abducted';

    // Disable normal movement
    const body = this.body as Phaser.Physics.Arcade.Body;
    if (body) {
      body.setAllowGravity(false);
    }
  }

  /**
   * Cancel abduction
   */
  cancelAbduction(): void {
    this.abductedState = 'normal';

    // Move down slightly
    this.y += 1;

    // Re-enable normal behavior
    if (this.definition.behavior === 'gravity') {
      const body = this.body as Phaser.Physics.Arcade.Body;
      if (body) {
        body.setAllowGravity(true);
      }
    }

    // Resume normal velocity
    this.setVelocityFromAngle(this.moveAngle, this.currentSpeed);
  }

  /**
   * Called when projectile's duration timer ends
   */
  private onDurationExpired(): void {
    // Check scene exists (may be destroyed during restart)
    if (!this.scene) return;

    // Some projectiles explode on expiry
    if (this.definition.behavior === 'homing' || this.projectileType === 'mine') {
      // Use onExpire effect if defined, otherwise fallback to onHit
      const fx = this.definition.effects?.onExpire || this.definition.effects?.onHit;
      if (fx) {
        const effectsSystem = this.scene.registry.get('effectsSystem') as EffectsSystem | undefined;
        effectsSystem?.spawn(fx, this.x, this.y);
      }

      // Handle AOE damage on expiry (for mines that explode after timeout)
      if (this.definition.aoeRadius && this.definition.aoeRadius > 0) {
        this.scene.events.emit('aoeExplosion', {
          x: this.x,
          y: this.y,
          radius: this.definition.aoeRadius,
          damage: this.definition.aoeDamage ?? this.damage,
          duration: this.definition.aoeDuration ?? 0,
          tint: this.definition.aoeTint,
          allegiance: this.allegiance,
          sourceTarget: undefined,
          attackType: this.definition.attackType,  // Pass attack type for weakTo check
        });
      }
    }
    this.returnToPool();
  }

  /**
   * Return projectile to pool for reuse
   */
  returnToPool(): void {
    this.isActive = false;
    this.setActive(false);
    this.setVisible(false);

    const body = this.body as Phaser.Physics.Arcade.Body;
    if (body) {
      body.setVelocity(0, 0);
      body.enable = false;
    }

    // Emit despawn event (check scene exists - may be destroyed during restart)
    if (this.scene?.events) {
      this.scene.events.emit('projectileDespawned', { projectile: this });
    }
  }

  /**
   * Reset projectile for reuse (pooling)
   */
  reset(config: ProjectileEntityConfig): void {
    // Update position
    this.setPosition(config.x ?? this.x, config.y ?? this.y);

    // Update allegiance
    this.allegiance = config.allegiance ?? Allegiance.Player;

    // Reset properties
    const def = DataManagerProjectile.get(config.projectileType) ?? this.definition;
    this.projectileType = config.projectileType;
    this.definition = def;
    this.damage = config.damage ?? def.damage;
    this.speed = config.speed ?? def.speed;
    this.currentSpeed = this.speed;
    this.moveAngle = config.angle;
    this.shooterId = config.shooterId;
    this.targetId = config.targetId;
    this.homingTarget = undefined;
    this.abductedState = 'normal';

    // Reset delayed lock state and travel phase
    this.delayedLockTimer = 0;

    // Check for travel phase in config
    if (config.travelTargetX !== undefined && config.travelTargetY !== undefined) {
      this.hasTravelPhase = true;
      this.travelTargetX = config.travelTargetX;
      this.travelTargetY = config.travelTargetY;
      this.travelSpeed = config.travelSpeed ?? 300;
      this.travelStartScale = config.travelStartScale ?? 0.1;
      this.travelEndScale = config.travelEndScale ?? (def.scale ?? 1);
      this.travelStartX = config.x ?? this.x;
      this.travelStartY = config.y ?? this.y;
      this.delayedLockState = 'traveling';
    } else {
      this.hasTravelPhase = false;
      this.delayedLockState = 'waiting';
    }

    // Reset destructible properties
    this.destructible = def.destructible ?? false;
    this.hp = def.hp ?? 0;

    // Reset texture if different
    if (this.texture.key !== def.texture) {
      this.setTexture(def.texture);
    }

    // Reset scale - travel phase starts small, otherwise use config or definition
    if (this.hasTravelPhase) {
      this.setScale(this.travelStartScale);
    } else if (config.initialScaleX !== undefined || config.initialScaleY !== undefined) {
      const sx = config.initialScaleX ?? def.scale ?? 1;
      const sy = config.initialScaleY ?? def.scale ?? 1;
      this.setScale(sx, sy);
    } else if (def.scale) {
      this.setScale(def.scale);
    } else {
      this.setScale(1);
    }

    // Reset expansion state for expanding projectiles
    this.baseScaleX = this.scaleX;
    this.baseScaleY = this.scaleY;
    this.expansionProgress = 0;

    // Reset origin: use definition override, fallback to C3 data
    const c3Origin = DataManagerProjectile.getOrigin(def.texture);
    const originX = def.originX ?? c3Origin.x;
    const originY = def.originY ?? c3Origin.y;
    this.setOrigin(originX, originY);

    // Update body size to match scaled display size
    this.syncBodyToVisualSize();

    // Reset rotation (sprites point right = 0° by default)
    this.setRotation(Phaser.Math.DegToRad(this.moveAngle));

    // Re-enable physics
    const body = this.body as Phaser.Physics.Arcade.Body;
    if (body) {
      body.enable = true;
      body.setAllowGravity(def.behavior === 'gravity');
    }

    // Re-enable
    this.setActive(true);
    this.setVisible(true);
    this.isActive = true;
    this.spawnTime = this.scene?.time?.now ?? 0;

    // Set frame or play animation
    if (def.frame) {
      this.setFrame(def.frame);
    } else if (def.animation) {
      // Normalize animation key to lowercase per project standards
      const animKey = Entity.normalize(def.animation);
      if (this.scene.anims.exists(animKey)) {
        this.play(animKey);
      }
    }
  }

  /**
   * Check if projectile is active
   */
  isAlive(): boolean {
    return this.isActive && this.active;
  }

  /**
   * Get the shooter entity if still exists
   */
  getShooter(): Entity | undefined {
    // This would need a reference to an entity registry
    // For now, return undefined
    return undefined;
  }

  /**
   * Handle animation complete (projectile-specific)
   */
  private onAnimationComplete(_anim: Phaser.Animations.Animation): void {
    // Override in subclasses if needed
  }
}
